import React from 'react';
import { Card, Table, Badge, Form } from 'react-bootstrap';
import { FaEye, FaPen, FaCheck } from 'react-icons/fa';
import './PermissionTableCard.css';

const isBooleanPage = (page) => page.boolean === true;
const isSelectPage = (page) => page.select === true;

const getSelectOptionValues = (page) =>
  (page.options || []).map((option) =>
    typeof option === 'object' ? String(option.value) : String(option)
  );

const isPageActive = (permissions, page) => {
  if (isBooleanPage(page)) {
    return permissions?.[page.key] === true;
  }
  if (isSelectPage(page)) {
    const value = permissions?.[page.key];
    const optionValues = getSelectOptionValues(page);
    if (optionValues.length === 0) return Boolean(value);
    return Boolean(value) && optionValues.includes(String(value));
  }
  return Boolean(permissions?.[page.key]?.read);
};

const ensurePagePermission = (permissions, pageKey) => {
  if (!permissions[pageKey]) {
    permissions[pageKey] = { read: false, write: false };
  }
};

const PermissionCheckbox = ({ checked, disabled, onChange, label }) => (
  <label className={`permission-check ${disabled ? 'permission-check--disabled' : ''}`}>
    <input
      type="checkbox"
      className="permission-check__input"
      checked={Boolean(checked)}
      onChange={onChange}
      disabled={disabled}
      aria-label={label}
    />
    <span className="permission-check__box">
      {checked && <FaCheck className="permission-check__icon" />}
    </span>
  </label>
);

const PermissionTableCard = ({
  title,
  icon: Icon,
  pages,
  permissions = {},
  canEdit,
  theme,
  onUpdate,
  accent = 'assistencia',
  showMasterRow = false,
  masterChecked = false,
  onMasterChange,
  rootSelects = [],
  lockedPageKeys = [],
  showToggleAll = true,
}) => {
  const lockedKeys = new Set(lockedPageKeys);
  const isPageLocked = (pageKey) => lockedKeys.has(pageKey);
  const readWritePages = pages.filter((page) => !isBooleanPage(page) && !isSelectPage(page));
  const editableReadWritePages = readWritePages.filter((page) => !isPageLocked(page.key));
  const rootSelectActiveCount = rootSelects.filter((select) => Boolean(select.value)).length;
  const activeCount =
    pages.filter((page) => isPageActive(permissions, page)).length + rootSelectActiveCount;
  const allRead =
    editableReadWritePages.length > 0 &&
    editableReadWritePages.every((page) => permissions?.[page.key]?.read);
  const allWrite =
    editableReadWritePages.length > 0 &&
    editableReadWritePages.every((page) => permissions?.[page.key]?.write);

  const handleToggleAll = (type, isChecked) => {
    if (!permissions) return;
    editableReadWritePages.forEach(({ key }) => {
      ensurePagePermission(permissions, key);
      if (type === 'read') {
        permissions[key].read = isChecked;
        if (!isChecked) permissions[key].write = false;
      } else {
        permissions[key].read = isChecked;
        permissions[key].write = isChecked;
      }
    });
    onUpdate();
  };

  const handleToggle = (pageKey, type, isChecked) => {
    if (!permissions) return;
    ensurePagePermission(permissions, pageKey);
    if (type === 'read') {
      permissions[pageKey].read = isChecked;
      if (!isChecked) permissions[pageKey].write = false;
    } else {
      permissions[pageKey].write = isChecked;
      if (isChecked) permissions[pageKey].read = true;
    }
    onUpdate();
  };

  const handleBooleanToggle = (pageKey, isChecked) => {
    if (!permissions) return;
    permissions[pageKey] = isChecked;
    onUpdate();
  };

  const handleSelectChange = (pageKey, value) => {
    if (!permissions) return;
    permissions[pageKey] = value;
    onUpdate();
  };

  return (
    <Card className={`permission-card permission-card--${accent} h-100 ${theme === 'dark' ? 'permission-card--dark' : ''}`}>
      <Card.Header className="permission-card__header">
        <div className="permission-card__header-inner">
          {Icon && (
            <div className={`permission-card__icon permission-card__icon--${accent}`}>
              <Icon />
            </div>
          )}
          <Card.Title className="permission-card__title mb-0">{title}</Card.Title>
          <Badge bg={activeCount > 0 ? 'success' : 'secondary'} className="permission-card__count">
            {activeCount}/{pages.length + rootSelects.length}
          </Badge>
        </div>
      </Card.Header>

      <Card.Body className="permission-card__body p-0">
        <Table
          size="sm"
          hover
          className={`permission-card__table mb-0 ${theme === 'dark' ? 'table-dark' : ''}`}
        >
          <thead>
            <tr>
              <th>Página</th>
              <th className="text-center">
                <span className="permission-card__th-icon permission-card__th-icon--read"><FaEye /></span>
                Ver
              </th>
              <th className="text-center">
                <span className="permission-card__th-icon permission-card__th-icon--write"><FaPen /></span>
                Editar
              </th>
            </tr>
          </thead>
          <tbody>
            {showMasterRow && (
              <tr className="permission-card__row-master">
                <td className="fw-semibold">Master</td>
                <td colSpan={2} className="text-center">
                  <PermissionCheckbox
                    checked={masterChecked}
                    disabled={!canEdit}
                    onChange={(e) => onMasterChange?.(e.target.checked)}
                    label="Master"
                  />
                </td>
              </tr>
            )}

            {showToggleAll && readWritePages.length > 0 && (
              <tr className="permission-card__row-all">
                <td className="fw-semibold">Todas</td>
                <td className="text-center">
                  <PermissionCheckbox
                    checked={allRead}
                    disabled={!canEdit}
                    onChange={(e) => handleToggleAll('read', e.target.checked)}
                    label="Todas — ver"
                  />
                </td>
                <td className="text-center">
                  <PermissionCheckbox
                    checked={allWrite}
                    disabled={!canEdit}
                    onChange={(e) => handleToggleAll('write', e.target.checked)}
                    label="Todas — editar"
                  />
                </td>
              </tr>
            )}

            {rootSelects.map((select) => (
              <tr
                key={select.key}
                className={select.value ? 'permission-card__row--write' : ''}
              >
                <td>{select.label}</td>
                <td colSpan={2} className="text-center">
                  <Form.Select
                    size="sm"
                    className="permission-card__select"
                    value={select.value || ''}
                    disabled={!canEdit}
                    onChange={(e) => select.onChange(e.target.value)}
                    aria-label={select.label}
                  >
                    <option value="">—</option>
                    {(select.options || []).map((option) => {
                      const optionValue = typeof option === 'object' ? option.value : option;
                      const optionLabel = typeof option === 'object' ? option.label : option;
                      return (
                        <option key={optionValue} value={optionValue}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </Form.Select>
                </td>
              </tr>
            ))}

            {pages.map((page) => {
              const { key, label } = page;
              const pageCanEdit = canEdit && !isPageLocked(key);

              if (isBooleanPage(page)) {
                const isActive = permissions?.[key] === true;
                return (
                  <tr key={key} className={isActive ? 'permission-card__row--write' : ''}>
                    <td>{label}</td>
                    <td colSpan={2} className="text-center">
                      <PermissionCheckbox
                        checked={isActive}
                        disabled={!pageCanEdit}
                        onChange={(e) => handleBooleanToggle(key, e.target.checked)}
                        label={`${label} — ativo`}
                      />
                    </td>
                  </tr>
                );
              }

              if (isSelectPage(page)) {
                const value = permissions?.[key] || '';
                const isActive = isPageActive(permissions, page);
                return (
                  <tr key={key} className={isActive ? 'permission-card__row--write' : ''}>
                    <td>{label}</td>
                    <td colSpan={2} className="text-center">
                      <Form.Select
                        size="sm"
                        className="permission-card__select"
                        value={value}
                        disabled={!pageCanEdit}
                        onChange={(e) => handleSelectChange(key, e.target.value)}
                        aria-label={label}
                      >
                        <option value="">—</option>
                        {(page.options || []).map((option) => {
                          const optionValue = typeof option === 'object' ? option.value : option;
                          const optionLabel = typeof option === 'object' ? option.label : option;
                          return (
                            <option key={optionValue} value={optionValue}>
                              {optionLabel}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </td>
                  </tr>
                );
              }

              const hasRead = permissions?.[key]?.read;
              const hasWrite = permissions?.[key]?.write;
              const rowClass = hasWrite ? 'permission-card__row--write' : hasRead ? 'permission-card__row--read' : '';

              return (
                <tr key={key} className={rowClass}>
                  <td>{label}</td>
                  <td className="text-center">
                    <PermissionCheckbox
                      checked={hasRead}
                      disabled={!pageCanEdit}
                      onChange={(e) => handleToggle(key, 'read', e.target.checked)}
                      label={`Ver: ${label}`}
                    />
                  </td>
                  <td className="text-center">
                    <PermissionCheckbox
                      checked={hasWrite}
                      disabled={!pageCanEdit}
                      onChange={(e) => handleToggle(key, 'write', e.target.checked)}
                      label={`Editar: ${label}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default PermissionTableCard;
